/**
 * Pantalla de elección obligatoria de sucursal activa (gate, ver
 * AppLocationGate). Solo las sucursales del scope REAL del usuario llegan acá
 * (GET /api/locations ya filtra por sm_user_locations) — nunca se ofrece una
 * sucursal a la que no tiene acceso.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Building2, Check } from "lucide-react";
import { useLocationContext } from "@/lib/location-context";
import { Loading } from "@/components/ds";

function parseElegirSucursalSearch(s: Record<string, unknown>): { returnTo?: string } {
  const out: { returnTo?: string } = {};
  if (typeof s.returnTo === "string" && s.returnTo.startsWith("/") && !s.returnTo.startsWith("//")) {
    out.returnTo = s.returnTo;
  }
  return out;
}

export const Route = createFileRoute("/app/elegir-sucursal")({
  validateSearch: parseElegirSucursalSearch,
  component: ElegirSucursalPage,
  head: () => ({ meta: [{ title: "Elegí tu sucursal — Structura Market" }] }),
});

function ElegirSucursalPage() {
  const { locations, activeLocationId, setActiveLocationId, loading } = useLocationContext();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const choose = (id: string) => {
    setActiveLocationId(id);
    void navigate({ to: search.returnTo ?? "/app/dashboard", replace: true });
  };

  if (loading) return <Loading />;

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-2xl flex-col items-center justify-center gap-6 px-4 py-16">
      <div className="text-center">
        <h1 className="text-xl font-bold text-foreground">¿En qué sucursal estás operando?</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Elegí una sucursal para continuar. Vas a ver solo lo que corresponde a esa sucursal.
        </p>
      </div>
      <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
        {locations.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => choose(l.id)}
            className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-foreground">{l.name}</span>
            </span>
            {l.id === activeLocationId && <Check className="h-4 w-4 shrink-0 text-primary" />}
          </button>
        ))}
      </div>
    </div>
  );
}
